import {makeObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {CurrencyModel} from '../../Currency/Currency.model';
import type {PaymentModel} from '../../Payment/Payment.model';

import {BaseModel} from '../../../../core/domain/Base/Base.model';
import {PriceModel} from '../../Price/Price.model';

const ModelName = 'PaymentPlanModel';

export class PaymentPlanModel extends BaseModel {
  currency: CurrencyModel;
  description: string;
  processing: PaymentModel.Processing;
  schedule: PaymentPlanModel.ScheduleItem[];
  total: PriceModel;

  constructor(props: PaymentPlanModel.Props) {
    super({id: ''});

    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<PaymentPlanModel, PaymentPlanModel.Props>(
        props,
      ),
    );
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  get monthlyPayment(): PriceModel {
    return this.paymentType === 'monthly'
      ? new PriceModel(
          Math.ceil(this.total.value / this.schedule.length),
          this.currency,
        )
      : new PriceModel(0, this.currency);
  }

  get paymentType(): PaymentPlanModel.PaymentType {
    return this.schedule.length > 1 && this.total.value > 0
      ? 'monthly'
      : 'one_time';
  }

  format({isShort}: {isShort: boolean} = {isShort: false}) {
    return this.paymentType === 'monthly'
      ? `${this.monthlyPayment.currencyString}${this.getMonthlyPaymentPerUnitString({isShort})}`
      : this.total.currencyString;
  }

  private getMonthlyPaymentPerUnitString(
    {isShort}: {isShort: boolean} = {isShort: false},
  ): string {
    if (isShort) {
      /**
       * @i18n_parser
       *
       * t('shared:program.duration_unit_short.day')
       * t('shared:program.duration_unit_short.week')
       * t('shared:program.duration_unit_short.month')
       */
      return i18n.t('shared:program.monthly_payment_per_unit_label_short', {
        unit: i18n.t('shared:program.duration_unit_short.month'),
      });
    }

    return i18n.t('shared:program.monthly_payment_per_unit_label', {
      unit: i18n.t('shared:program.duration_unit.month', {
        count: 1,
      }),
    });
  }
}

export namespace PaymentPlanModel {
  export type PaymentType = 'monthly' | 'one_time';

  export type Props = BaseModel.ModelToProps<
    PaymentPlanModel,
    'format' | 'monthlyPayment' | 'paymentType'
  >;

  export type ScheduleItem = {
    amount: PriceModel;
    day: number;
    uuid: string;
  };
}
